import { useState } from 'react';
import { MdDownload } from 'react-icons/md';
import { Role } from '@blisspointmedia/bpm-types/dist/Users';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import { exportToExcel } from '@src/utils/download-utils';
import { CreativeLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { useSetError } from '@src/redux/modals';
import { useAppSelector } from '@src/redux';
import * as UserRedux from '@src/redux/user';
import { RAW_DATA_VALUE_NUMBER_FORMAT, RawDataTypeType } from './creative-insights-constants';

interface RawDataDownloadButtonProps {
  dataType: RawDataTypeType;
  kpi: string;
  fileName: string;
  nameColumn: string;
  valueColumn: string;
  subject: string;
}

export const RawDataDownloadButton = ({
  dataType,
  kpi,
  fileName,
  nameColumn,
  valueColumn,
  subject
}: RawDataDownloadButtonProps) => {
  const userRole = useAppSelector(UserRedux.roleSelector);
  const [downloading, setDownloading] = useState(false);
  const setError = useSetError();

  if (userRole > Role.INTERNAL) {
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const rows = await awaitJSON<string[][]>(
        await CreativeLambdaFetch('/fetchRawCreativeInsightsData', { params: { dataType, kpi } })
      );
      await exportToExcel(
        rows.map(([name, value]) => ({ [nameColumn]: name, [valueColumn]: Number(value) })),
        fileName,
        { [valueColumn]: RAW_DATA_VALUE_NUMBER_FORMAT }
      );
    } catch (e) {
      setError({
        message: `Couldn't download ${subject}. Error: ${e.message}.`,
        reportError: e
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <IconButton
      data-testid={`raw-data-download-${dataType}`}
      loading={downloading}
      size={Size.MD}
      onClick={handleDownload}
      aria-label={`Download ${subject}`}
      tooltip={{ text: `Download ${subject}` }}
    >
      <MdDownload />
    </IconButton>
  );
};
